<script lang="ts">
  import { userEmail, currentView } from './stores';
  import { formatFileSize } from './format';
  import { submitBugReport, type BugReportAttachment } from './api';

  let { open = $bindable(false) }: { open: boolean } = $props();

  const MAX_FILES = 5;
  const MAX_PER_FILE = 5 * 1024 * 1024;       // 5 MB
  const MAX_TOTAL = 15 * 1024 * 1024;         // 15 MB

  let title = $state('');
  let body = $state('');
  let attachments = $state<File[]>([]);
  let submitting = $state(false);
  let success = $state(false);
  let error = $state('');

  let totalBytes = $derived(attachments.reduce((sum, f) => sum + f.size, 0));

  function close() {
    if (submitting) return;
    open = false;
    setTimeout(() => {
      title = '';
      body = '';
      attachments = [];
      success = false;
      error = '';
    }, 200);
  }


  function onFilesPicked(e: Event) {
    error = '';
    const input = e.target as HTMLInputElement;
    const picked = Array.from(input.files || []);
    const next = [...attachments];
    for (const f of picked) {
      if (next.length >= MAX_FILES) {
        error = `Max ${MAX_FILES} files.`;
        break;
      }
      if (f.size > MAX_PER_FILE) {
        error = `${f.name} exceeds 5 MB limit.`;
        continue;
      }
      const newTotal = next.reduce((s, x) => s + x.size, 0) + f.size;
      if (newTotal > MAX_TOTAL) {
        error = 'Total attachments would exceed 15 MB.';
        break;
      }
      next.push(f);
    }
    attachments = next;
    // Reset the input so the same file can be re-selected after removal
    input.value = '';
  }

  function removeAttachment(idx: number) {
    attachments = attachments.filter((_, i) => i !== idx);
  }

  function fileToB64(f: File): Promise<BugReportAttachment> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the "data:<type>;base64," prefix
        const idx = result.indexOf(',');
        const data_b64 = idx >= 0 ? result.slice(idx + 1) : result;
        resolve({
          filename: f.name,
          content_type: f.type || 'application/octet-stream',
          data_b64,
        });
      };
      reader.onerror = () => reject(reader.error || new Error('File read failed'));
      reader.readAsDataURL(f);
    });
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (!body.trim()) {
      error = 'Please describe the bug.';
      return;
    }
    submitting = true;
    error = '';
    try {
      const encoded = await Promise.all(attachments.map(fileToB64));
      const r = await submitBugReport({
        title: title.trim().slice(0, 200),
        body: body.trim(),
        page: $currentView || (typeof window !== 'undefined' ? window.location.hash : ''),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        attachments: encoded,
      });
      if (r.delivered) {
        success = true;
        setTimeout(close, 1800);
      } else {
        error = r.error || 'Bug report email could not be delivered. Please try again.';
      }
    } catch (e: any) {
      error = e?.message || 'Submit failed.';
    }
    submitting = false;
  }
</script>

{#if open}
<div class="bug-overlay" onclick={close} role="presentation">
  <div class="bug-modal" onclick={(e) => e.stopPropagation()} role="dialog" aria-label="Report a bug">
    <button class="bug-close" onclick={close} aria-label="Close">&times;</button>

    {#if success}
      <h2 style="margin-bottom: 8px;">Thanks — sent.</h2>
      <p class="text-dim" style="font-size: 13px;">
        Daniel will see this in his inbox shortly. Closing…
      </p>
    {:else}
      <h2 style="margin-bottom: 4px;">🐞 Report a bug</h2>
      <p class="text-dim" style="font-size: 13px; margin-bottom: 16px;">
        Reporting as <strong>{$userEmail}</strong>. Goes straight to <code>daniel@dealton.ai</code> with the page and browser info attached.
      </p>

      <form onsubmit={submit}>
        <label class="bug-field">
          <span class="bug-label">Title (optional)</span>
          <input type="text" bind:value={title} maxlength="200"
            placeholder="Short summary of the issue" />
        </label>

        <label class="bug-field">
          <span class="bug-label">What happened?</span>
          <textarea bind:value={body} rows="6" maxlength="10000"
            placeholder="Steps to reproduce, what you expected, what actually happened…"
            required></textarea>
        </label>

        <div class="bug-field">
          <span class="bug-label">
            Attachments
            <span class="bug-attach-meta">
              {attachments.length}/{MAX_FILES} files · {formatFileSize(totalBytes)} / 15 MB
            </span>
          </span>

          {#if attachments.length > 0}
            <ul class="bug-attach-list">
              {#each attachments as f, idx}
                <li>
                  <span class="bug-attach-name"><svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{f.name}</span>
                  <span class="bug-attach-size">{formatFileSize(f.size)}</span>
                  <button type="button" class="bug-attach-remove"
                    onclick={() => removeAttachment(idx)} aria-label="Remove">×</button>
                </li>
              {/each}
            </ul>
          {/if}

          <label class="bug-attach-pick"
            class:bug-attach-pick-disabled={attachments.length >= MAX_FILES}>
            <input type="file" multiple onchange={onFilesPicked}
              disabled={attachments.length >= MAX_FILES}
              style="display:none;" />
            {attachments.length === 0 ? '+ Add screenshots or files' : '+ Add more'}
          </label>
        </div>

        {#if error}
          <p class="bug-error">{error}</p>
        {/if}

        <div class="bug-actions">
          <button type="button" class="bug-cancel" onclick={close} disabled={submitting}>Cancel</button>
          <button type="submit" class="bug-submit" disabled={submitting || !body.trim()}>
            {submitting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    {/if}
  </div>
</div>
{/if}

<style>
  .bug-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .bug-modal {
    background: var(--bg-surface, #fff);
    border-radius: 12px;
    padding: 28px 32px 24px;
    max-width: 520px;
    width: 90%;
    position: relative;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }
  .bug-close {
    position: absolute;
    top: 12px;
    right: 16px;
    background: none;
    border: none;
    font-size: 22px;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px 8px;
  }
  .bug-close:hover { color: var(--text); }
  .bug-field {
    display: block;
    margin-bottom: 14px;
  }
  .bug-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    margin-bottom: 4px;
  }
  .bug-field input, .bug-field textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    background: var(--bg-surface, #fff);
    color: var(--text);
    box-sizing: border-box;
  }
  .bug-field textarea { resize: vertical; min-height: 96px; }
  .bug-error {
    color: #ef4444;
    font-size: 12px;
    margin: 8px 0 12px;
  }
  .bug-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .bug-cancel, .bug-submit {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--border);
  }
  .bug-cancel { background: transparent; color: var(--text-muted); }
  .bug-submit {
    background: var(--accent, #3b82f6);
    color: #fff;
    border-color: transparent;
  }
  .bug-submit:disabled, .bug-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Attachment UI */
  .bug-attach-meta {
    color: var(--text-muted);
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    margin-left: 6px;
    font-size: 11px;
  }
  .bug-attach-list {
    list-style: none;
    padding: 0;
    margin: 6px 0 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .bug-attach-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: var(--bg-hover);
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 13px;
  }
  .bug-attach-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bug-attach-size { color: var(--text-muted); font-size: 12px; }
  .bug-attach-remove {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;
  }
  .bug-attach-remove:hover { color: #ef4444; }
  .bug-attach-pick {
    display: inline-block;
    margin-top: 4px;
    padding: 6px 12px;
    border: 1px dashed var(--border);
    border-radius: 6px;
    color: var(--text-muted);
    font-size: 12px;
    cursor: pointer;
  }
  .bug-attach-pick:hover { background: var(--bg-hover); color: var(--text); }
  .bug-attach-pick-disabled { opacity: 0.5; cursor: not-allowed; }
  .bug-attach-pick-disabled:hover { background: transparent; color: var(--text-muted); }
</style>
